import { createBrowserRouter, Navigate } from 'react-router-dom';
import type { CrumbHandle } from './layouts/breadcrumbs';
import { ErrorPage } from './routes/error/page';

type ReactRouter = ReturnType<typeof createBrowserRouter>;

/**
 * Builds the breadcrumb `handle` of a route from `[labelKey, path?]` pairs
 * (see `CrumbHandle` in layouts/breadcrumbs).
 */
const crumbs = (...entries: [string, string?][]): CrumbHandle => ({
  crumbs: entries.map(([labelKey, path]) => ({ labelKey, path })),
});

export const createRouter = (
  createRouterFn = createBrowserRouter,
): ReactRouter =>
  createRouterFn([
    // Main application routes (unified layout)
    {
      path: '/',
      errorElement: <ErrorPage />,
      lazy: async () => {
        const Component = await import('./layouts/main.layout').then(
          (e) => e.MainLayout,
        );
        return { Component };
      },
      children: [
        {
          path: '',
          handle: crumbs(['admin.menu.dashboard']),
          lazy: async () => {
            const { DashboardRouter } = await import(
              './routes/dashboard/dashboard-router'
            );
            return { Component: DashboardRouter };
          },
        },
        {
          path: 'unauthorized',
          lazy: async () => {
            const Component = await import(
              './routes/admin/unauthorized/page'
            ).then((e) => e.AdminUnauthorized);
            return { Component };
          },
        },
        {
          path: 'sites',
          handle: crumbs(['admin.menu.sites']),
          lazy: async () => {
            const Component = await import('./routes/admin/sites/page').then(
              (e) => e.AdminSites,
            );
            return { Component };
          },
        },
        {
          path: 'sites/:siteId',
          handle: crumbs(
            ['admin.menu.sites', '/sites'],
            ['layout.breadcrumb.detail'],
          ),
          lazy: async () => {
            const { AdminSiteDetail } = await import(
              './routes/admin/sites/[id]/page'
            );
            return { Component: AdminSiteDetail };
          },
        },
        {
          path: 'assistants',
          handle: crumbs(['admin.menu.assistants']),
          lazy: async () => {
            const { AdminAssistants } = await import(
              './routes/admin/assistants/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canManageAssistants">
                  <AdminAssistants />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'assistants/planning',
          handle: crumbs(
            ['admin.menu.assistants', '/assistants'],
            ['layout.breadcrumb.assistants-planning'],
          ),
          lazy: async () => {
            const { AdminAssistantsPlanning } = await import(
              './routes/admin/assistants/planning/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canManageAssistants">
                  <AdminAssistantsPlanning />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'assistants/absences',
          handle: crumbs(
            ['admin.menu.assistants', '/assistants'],
            ['assistant.menu.absences'],
          ),
          lazy: async () => {
            const { AdminAssistantAbsences } = await import(
              './routes/admin/assistants/absences/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canManageAssistants">
                  <AdminAssistantAbsences />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'assistants/:id',
          handle: crumbs(
            ['admin.menu.assistants', '/assistants'],
            ['layout.breadcrumb.detail'],
          ),
          lazy: async () => {
            const { AdminAssistantDetail } = await import(
              './routes/admin/assistants/[id]/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canManageAssistants">
                  <AdminAssistantDetail />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'offices',
          handle: crumbs(['admin.menu.offices']),
          lazy: async () => {
            const Component = await import('./routes/admin/offices/page').then(
              (e) => e.AdminOffices,
            );
            return { Component };
          },
        },
        {
          path: 'offices/:officeId',
          handle: crumbs(
            ['admin.menu.offices', '/offices'],
            ['layout.breadcrumb.detail'],
          ),
          lazy: async () => {
            const { AdminOfficeDetail } = await import(
              './routes/admin/offices/[id]/page'
            );
            return { Component: AdminOfficeDetail };
          },
        },
        {
          path: 'doctor-substitutes',
          handle: crumbs(['admin.menu.doctors']),
          lazy: async () => {
            const Component = await import(
              './routes/admin/doctor-substitutes/page'
            ).then((e) => e.AdminDoctorSubstitutes);
            return { Component };
          },
        },
        {
          path: 'doctor-substitutes/:doctorId',
          handle: crumbs(
            ['admin.menu.doctors', '/doctor-substitutes'],
            ['layout.breadcrumb.detail'],
          ),
          lazy: async () => {
            const { AdminDoctorSubstituteDetail } = await import(
              './routes/admin/doctor-substitutes/[id]/page'
            );
            return { Component: AdminDoctorSubstituteDetail };
          },
        },
        {
          path: 'doctor-attendings',
          handle: crumbs(['admin.menu.doctor-attendings']),
          lazy: async () => {
            const Component = await import(
              './routes/admin/doctor-attendings/page'
            ).then((e) => e.AdminDoctorAttendings);
            return { Component };
          },
        },
        {
          path: 'doctor-attendings/:doctorId',
          handle: crumbs(
            ['admin.menu.doctor-attendings', '/doctor-attendings'],
            ['layout.breadcrumb.detail'],
          ),
          lazy: async () => {
            const { AdminDoctorAttendingDetail } = await import(
              './routes/admin/doctor-attendings/[id]/page'
            );
            return { Component: AdminDoctorAttendingDetail };
          },
        },
        {
          path: 'prospects',
          handle: crumbs(['admin.menu.prospects']),
          lazy: async () => {
            const { AdminProspects } = await import(
              './routes/admin/prospects/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canViewProspects">
                  <AdminProspects />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'nous-rejoindre',
          handle: crumbs(
            ['layout.sidebar.group-recruitment'],
            ['referrals.menu.join-us'],
          ),
          lazy: async () => {
            const { UserNousRejoindre } = await import(
              './routes/user/nous-rejoindre/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canViewRecruitmentOffer">
                  <UserNousRejoindre />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'parrainage',
          handle: crumbs(
            ['layout.sidebar.group-recruitment'],
            ['referrals.menu.referral-program'],
          ),
          lazy: async () => {
            const { UserParrainage } = await import(
              './routes/user/parrainage/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canUseReferralProgram">
                  <UserParrainage />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'contracts',
          handle: crumbs(['user.menu.contracts']),
          lazy: async () => {
            const { UserContracts } = await import(
              './routes/user/contracts/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canViewContracts">
                  <UserContracts />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'contracts/new',
          handle: crumbs(
            ['user.menu.contracts', '/contracts'],
            ['layout.breadcrumb.contract-new'],
          ),
          lazy: async () => {
            const { UserContractsCreate } = await import(
              './routes/user/contracts/new/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canViewContracts">
                  <UserContractsCreate />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'contracts/:contractId',
          handle: crumbs(
            ['user.menu.contracts', '/contracts'],
            ['layout.breadcrumb.detail'],
          ),
          lazy: async () => {
            const { UserContractDetail } = await import(
              './routes/user/contracts/[id]/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canViewContracts">
                  <UserContractDetail />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          // Trackers get the administration list, readers their own space.
          path: 'documents',
          handle: crumbs(['admin.menu.documents']),
          lazy: async () => {
            const { DocumentsRoute } = await import(
              './routes/documents/documents-route'
            );
            return { Component: DocumentsRoute };
          },
        },
        {
          path: 'documents/new',
          handle: crumbs(
            ['admin.menu.documents', '/documents'],
            ['admin.documents.publish.breadcrumb'],
          ),
          lazy: async () => {
            const { AdminPublishDocument } = await import(
              './routes/admin/documents/new/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canManageDocuments">
                  <AdminPublishDocument />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          // The administration's follow-up of one document; readers never
          // reach it (no reading role holds `can_view_documents`).
          path: 'documents/:documentId',
          handle: crumbs(
            ['admin.menu.documents', '/documents'],
            ['admin.documents.detail.breadcrumb'],
          ),
          lazy: async () => {
            const { AdminDocumentDetail } = await import(
              './routes/admin/documents/[id]/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canViewDocuments">
                  <AdminDocumentDetail />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'assistant/planning',
          handle: crumbs(['assistant.menu.planning']),
          lazy: async () => {
            const { AssistantPlanning } = await import(
              './routes/assistant/planning/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canViewAssistantsPlanning">
                  <AssistantPlanning />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'assistant/creneaux',
          handle: crumbs(['assistant.menu.creneaux']),
          lazy: async () => {
            const { AssistantCreneaux } = await import(
              './routes/assistant/creneaux/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canViewSlots">
                  <AssistantCreneaux />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'assistant/absences',
          handle: crumbs(['assistant.menu.absences']),
          lazy: async () => {
            const { AssistantAbsences } = await import(
              './routes/assistant/absences/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canViewAssistantsPlanning">
                  <AssistantAbsences />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'assistant/documents',
          handle: crumbs(['documents.reader.menu']),
          lazy: async () => {
            const { ReaderDocuments } = await import('./routes/documents/page');
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canReadDocuments">
                  <ReaderDocuments />
                </CapabilityRoute>
              ),
            };
          },
        },
        {
          path: 'agendas',
          handle: crumbs(['admin.menu.slots']),
          lazy: async () => {
            const Component = await import(
              './routes/admin/agendas-index/page'
            ).then((e) => e.AdminAgendasIndex);
            return { Component };
          },
        },
        {
          path: 'accountings',
          handle: crumbs(['admin.menu.accountings']),
          lazy: async () => {
            const { AdminAccountings } = await import(
              './routes/admin/accountings/page'
            );
            const { CapabilityRoute } = await import(
              './components/guards/capability-route'
            );
            return {
              Component: () => (
                <CapabilityRoute capability="canAccessAccounting">
                  <AdminAccountings />
                </CapabilityRoute>
              ),
            };
          },
        },
      ],
    },
    // Legacy /admin/* redirects for backwards compatibility
    {
      path: '/admin',
      element: (
        <Navigate
          to="/"
          replace
        />
      ),
    },
    {
      path: '/admin/sites',
      element: (
        <Navigate
          to="/sites"
          replace
        />
      ),
    },
    {
      path: '/admin/assistants',
      element: (
        <Navigate
          to="/assistants"
          replace
        />
      ),
    },
    {
      path: '/admin/offices',
      element: (
        <Navigate
          to="/offices"
          replace
        />
      ),
    },
    {
      path: '/admin/doctor-substitutes',
      element: (
        <Navigate
          to="/doctor-substitutes"
          replace
        />
      ),
    },
    {
      path: '/admin/doctor-attendings',
      element: (
        <Navigate
          to="/doctor-attendings"
          replace
        />
      ),
    },
    {
      path: '/admin/agendas',
      element: (
        <Navigate
          to="/agendas"
          replace
        />
      ),
    },
    {
      path: '/admin/accountings',
      element: (
        <Navigate
          to="/accountings"
          replace
        />
      ),
    },
    {
      path: '/admin/unauthorized',
      element: (
        <Navigate
          to="/unauthorized"
          replace
        />
      ),
    },
    {
      path: '/login',
      lazy: async () => {
        const Component = await import('./routes/auth/login.route').then(
          (e) => e.Login,
        );
        return { Component };
      },
    },
    {
      path: '/forgot-password',
      lazy: async () => {
        const Component = await import('./layouts/auth.layout').then(
          (e) => e.AuthPasswordLayout,
        );
        return { Component };
      },

      children: [
        {
          path: '',
          lazy: async () => {
            const Component = await import(
              './routes/auth/forgot-password/forgot-password'
            ).then((e) => e.ForgotPassword);
            return { Component };
          },
        },
        {
          path: 'confirm',
          lazy: async () => {
            const Component = await import(
              './routes/auth/forgot-password/forgot-password-confirm'
            ).then((e) => e.ForgotPasswordConfirm);
            return { Component };
          },
        },
      ],
    },
    {
      path: '/reset-password',
      lazy: async () => {
        const Component = await import('./layouts/auth.layout').then(
          (e) => e.AuthPasswordLayout,
        );
        return { Component };
      },
      children: [
        {
          path: '',
          lazy: async () => {
            const Component = await import(
              './routes/auth/reset-password/reset-password'
            ).then((e) => e.ResetPassword);
            return { Component };
          },
        },
      ],
    },
    {
      path: '/register',
      lazy: async () => {
        const Component = await import('./layouts/register.layout').then(
          (e) => e.RegisterLayout,
        );
        return { Component };
      },
      children: [
        {
          path: 'doctor-substitute',
          lazy: async () => {
            const Component = await import(
              './routes/register/register-doctor-substitute'
            ).then((e) => e.RegisterDoctorSubstitute);
            return { Component };
          },
        },
        {
          path: 'doctor-attending',
          lazy: async () => {
            const Component = await import(
              './routes/register/register-doctor-attending'
            ).then((e) => e.RegisterDoctorAttending);
            return { Component };
          },
        },
      ],
    },
    {
      path: '/offices/:officeId/planning',
      lazy: async () => {
        const Component = await import(
          './components/templates/planning/page'
        ).then((e) => e.OfficePlanning);
        return { Component };
      },
    },
    {
      path: '/accounting/:accountingId/retrocession-fee',
      lazy: async () => {
        const Component = await import(
          '@/components/templates/accounting-retrocession-fee/page'
        ).then((e) => e.AccountingRetrocessionFee);
        return { Component };
      },
    },
    {
      path: '/accounting-doctor-attending',
      lazy: async () => {
        const Component = await import(
          '@/components/templates/accounting-doctor-attending/page'
        ).then((e) => e.AccountingDoctorAttending);
        return { Component };
      },
    },
  ]);

export type AppRouter = ReturnType<typeof createRouter>;
